import { useState, useEffect, useRef } from 'react';
import { guestService, companyService, eventService } from 'services/api';
import SearchableSelect from 'components/SearchableSelect';
import ImportModal from './ImportModal';
import InviteModal from './InviteModal';
import Badge from './Badge';
import QrScanner from './QrScanner';
import Paginacion from 'components/Paginacion';
import styles from './Guests.module.css';

// Normaliza checked_in al nivel raíz independiente de la versión del backend
const normalizeGuest = (g) => ({
  ...g,
  checked_in: g.checked_in === 1 || g.checked_in === true ||
    g.event_guest?.checked_in === 1 ||
    g.event_guest?.checked_in === true || false,
});

const PAGE_SIZE = 50;

// ── Componente principal ───────────────────────────────────────────────────────
export default function Guests() {
  const [items,      setItems]      = useState([]);
  const [companies,  setCompanies]  = useState([]);
  const [events,     setEvents]     = useState([]);
  const [filterComp, setFilterComp] = useState('');
  const [filterEvt,  setFilterEvt]  = useState('');
  const [loading,    setLoading]    = useState(false);
  const [alert,      setAlert]      = useState(null);
  const [importModal, setImportModal] = useState(false);
  const [badgeGuest,  setBadgeGuest]  = useState(null);
  const [checkingIn,  setCheckingIn]  = useState(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [inviteModal, setInviteModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  // Guard síncrono contra doble-click: `checkingIn` (estado) tarda un ciclo de
  // render en deshabilitar el botón, así que un doble-click rápido puede
  // disparar handleCheckin() dos veces antes de que React re-renderice —
  // con presentCount ahora como contador manual (no derivado de items, ver
  // abajo), eso duplicaba el +1/-1. Un ref es síncrono, cierra la ventana.
  const checkingInRef = useRef(new Set());

  // Paginación de la lista completa
  const [page,         setPage]         = useState(1);
  const [lastPage,     setLastPage]     = useState(1);
  const [total,        setTotal]        = useState(0);
  const [presentCount, setPresentCount] = useState(0);

  // Buscador de check-in (searchResults === null significa "no está buscando")
  const [searchQuery,   setSearchQuery]   = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching,     setSearching]     = useState(false);

  useEffect(() => {
    companyService.getAll({ limit: 200 }).then(r => setCompanies(r.data?.data || r.data || [])).catch(() => {});
  }, []);

  const loadEvents = (cid) => {
    setFilterComp(cid); setFilterEvt(''); setEvents([]); setItems([]);
    if (!cid) return;
    eventService.getAll({ company_id: cid })
      .then(r => setEvents(r.data?.data || r.data || [])).catch(() => {});
  };

  const irAPagina = (n) => { load(filterEvt, n); window.scrollTo({ top: 0 }); };

  const load = (eid = filterEvt, pageArg = 1) => {
    if (!eid) { setItems([]); setTotal(0); setPresentCount(0); return; }
    setLoading(true);
    guestService.getAll({ event_id: eid, page: pageArg, limit: PAGE_SIZE })
      .then(r => {
        const body = r.data?.data ? r.data : { data: r.data || [] }; // compat por si acaso
        setItems((body.data || []).map(normalizeGuest));
        setTotal(body.total ?? body.data.length);
        setPage(body.page ?? pageArg);
        setLastPage(body.lastPage ?? 1);
        setPresentCount(body.presentCount ?? 0);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    setSearchQuery(''); setSearchResults(null); setSelectedIds(new Set());
    load(filterEvt, 1);
  }, [filterEvt]); // eslint-disable-line

  const toggleSelected = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Buscador — debounce de 300ms, reemplaza la vista paginada mientras hay texto
  useEffect(() => {
    if (!filterEvt || searchQuery.trim().length < 2) { setSearchResults(null); return; }
    setSearching(true);
    const t = setTimeout(() => {
      guestService.search(filterEvt, searchQuery.trim())
        .then(r => setSearchResults((r.data || []).map(normalizeGuest)))
        .catch(() => setSearchResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [searchQuery, filterEvt]);

  const displayedGuests = searchResults !== null ? searchResults : items;

  const handleCheckin = async (guest) => {
    if (checkingInRef.current.has(guest.id)) return; // ya hay una llamada en vuelo para este invitado
    checkingInRef.current.add(guest.id);
    setCheckingIn(guest.id);
    try {
      const newState = !guest.checked_in;
      if (guest.checked_in) {
        await guestService.undoCheckin(guest.id, filterEvt);
        setAlert({ type: 'success', message: `Check-in de ${guest.name} revertido` });
      } else {
        await guestService.checkin(guest.id, filterEvt);
        setAlert({ type: 'success', message: `✓ ${guest.name} registrado` });
      }
      // Actualizar inmediatamente en el estado local sin esperar recarga —
      // en ambas vistas (paginada y búsqueda), la que no esté activa no se
      // muestra pero igual se mantiene consistente si el usuario vuelve a ella.
      const patch = g => g.id === guest.id ? { ...g, checked_in: newState } : g;
      setItems(prev => prev.map(patch));
      setSearchResults(prev => prev ? prev.map(patch) : prev);
      setPresentCount(prev => prev + (newState ? 1 : -1));
    } catch (err) {
      setAlert({ type: 'danger', message: err.response?.data?.message || 'Error en check-in' });
    } finally {
      checkingInRef.current.delete(guest.id);
      setCheckingIn(null);
    }
  };

  const selectedEvent   = events.find(e => String(e.id) === String(filterEvt));
  const selectedCompany = companies.find(c => String(c.id) === String(filterComp));

  return (
    <div>
      <div className="page-header">
        <div>
          <h1><i className={`fas fa-id-badge ${styles.iconoTitulo}`} />Invitados</h1>
          <p>Gestiona invitados por compañía y evento</p>
        </div>
        <div className={styles.accionesEncabezado}>
          {filterEvt && (
            <>
              <button className="btn btn-secondary" onClick={() => setInviteModal(true)}>
                <i className="fas fa-paper-plane" /> Enviar invitación
              </button>
              <button className="btn btn-secondary" onClick={() => setScannerOpen(true)}>
                <i className="fas fa-qrcode" /> Escanear QR
              </button>
            </>
          )}
          <button className="btn btn-secondary" onClick={() => setImportModal(true)}>
            <i className="fas fa-file-excel" /> Importar Excel
          </button>
        </div>
      </div>

      {alert && (
        <div className={`alert alert-${alert.type} ${styles.alertaClic}`} onClick={() => setAlert(null)}>
          {alert.message}
        </div>
      )}

      {/* KPIs — solo si hay evento seleccionado */}
      {filterEvt && (
        <div className={styles.kpis}>
          {[
            { label:'Total invitados', value:total,                 icon:'fa-users',        color:'#6c63ff' },
            { label:'Presentes',       value:presentCount,          icon:'fa-circle-check', color:'#12b76a' },
            { label:'Pendientes',      value:total - presentCount,  icon:'fa-clock',        color:'#f79009' },
          ].map(k => (
            <div key={k.label} className={styles.kpi} style={{ '--kpi': k.color, '--kpi-suave': k.color + '22' }}>
              <div className={styles.kpiIcono}>
                <i className={`fas ${k.icon} ${styles.kpiGlifo}`} />
              </div>
              <div>
                <div className={styles.kpiValor}>{k.value}</div>
                <div className={styles.kpiEtiqueta}>{k.label}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="card">
        <div className={styles.barraFiltros}>
          {/* Filtro compañía */}
          <div className={styles.filtro}>
            <i className={`fas fa-briefcase ${styles.iconoFiltro}`} />
            <SearchableSelect value={filterComp} onChange={(id) => loadEvents(id)} placeholder="— Selecciona compañía —" minWidth={220} ariaLabel="Compañía"
              options={companies.map(c => ({ value: c.id, label: c.name }))} />
          </div>
          {/* Filtro evento */}
          <div className={styles.filtro}>
            <i className={`fas fa-calendar-star ${styles.iconoFiltro}`} />
            <SearchableSelect value={filterEvt} onChange={(id) => setFilterEvt(id)} disabled={!filterComp || !events.length} minWidth={220} ariaLabel="Evento"
              placeholder={!filterComp ? '— Primero selecciona compañía —' : events.length===0 ? 'Sin eventos' : '— Selecciona evento —'}
              options={events.map(ev => ({ value: ev.id, label: ev.name }))} />
          </div>
          {/* Buscador — para eventos grandes, la forma real de encontrar a alguien */}
          {filterEvt && (
            <div className={styles.filtroBusqueda}>
              <i className={`fas fa-search ${styles.iconoFiltro}`} />
              <input
                type="text"
                className={`form-control ${styles.campoBusqueda}`}
                placeholder="Buscar por nombre o documento..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
              {searching && <i className={`fas fa-spinner fa-spin ${styles.spinnerBusqueda}`} />}
              {searchQuery && (
                <button className="btn btn-sm btn-secondary btn-icon" onClick={() => setSearchQuery('')} title="Limpiar búsqueda">
                  <i className="fas fa-times" />
                </button>
              )}
            </div>
          )}
          {filterEvt && !loading && (
            <span className={styles.conteo}>
              {searchResults !== null
                ? `${searchResults.length} resultado(s) de la búsqueda`
                : `${total} invitado(s) — página ${page} de ${lastPage}`}
            </span>
          )}
        </div>

        {!filterComp ? (
          <div className="empty-state"><i className="fas fa-briefcase" /><p>Selecciona una compañía para continuar</p></div>
        ) : !filterEvt ? (
          <div className="empty-state"><i className="fas fa-calendar-star" /><p>Selecciona un evento para ver sus invitados</p></div>
        ) : loading ? (
          <div className="loading"><div className="spinner" /></div>
        ) : displayedGuests.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-id-badge" />
            <p>{searchResults !== null ? 'Sin resultados para esa búsqueda.' : 'Sin invitados para este evento. Importa desde Excel.'}</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th className={styles.colCheck}>
                    <input type="checkbox"
                      checked={displayedGuests.length > 0 && displayedGuests.every(g => selectedIds.has(g.id))}
                      onChange={e => {
                        setSelectedIds(prev => {
                          const next = new Set(prev);
                          displayedGuests.forEach(g => e.target.checked ? next.add(g.id) : next.delete(g.id));
                          return next;
                        });
                      }}
                      title="Seleccionar todos en esta página" />
                  </th>
                  <th>Invitado</th>
                  <th>Documento</th>
                  <th>Cargo / Empresa</th>
                  <th>Contacto</th>
                  <th className={styles.centrado}>Asistencia</th>
                  <th className={styles.centrado}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {displayedGuests.map(g => (
                  <tr key={g.id} className={g.checked_in ? styles.filaPresente : undefined}>
                    <td>
                      <input type="checkbox" checked={selectedIds.has(g.id)} onChange={() => toggleSelected(g.id)} />
                    </td>
                    <td>
                      <div className={styles.nombre}>{g.name}</div>
                      {g.invited_at && (
                        <div className={styles.marcaInvitado}>
                          <i className={`fas fa-envelope ${styles.marcaInvitadoIcono}`} />Invitado
                        </div>
                      )}
                    </td>
                    <td className={styles.celdaDocumento}>
                      <span className={styles.tipoDocumento}>{g.type_identification || 'CC'}</span>
                      {g.number || '—'}
                    </td>
                    <td>
                      <div className={styles.cargo}>{g.position_company || '—'}</div>
                      {g.company && <div className={styles.empresa}>{g.company}</div>}
                    </td>
                    <td className={styles.celdaContacto}>
                      {g.email && <div>{g.email}</div>}
                      {g.phone && <div>{g.phone}</div>}
                    </td>
                    <td className={styles.centrado}>
                      {g.checked_in ? (
                        <span className={`${styles.estado} ${styles.estadoPresente}`}>
                          <i className={`fas fa-circle-check ${styles.estadoIcono}`} /> Presente
                        </span>
                      ) : (
                        <span className={`${styles.estado} ${styles.estadoPendiente}`}>
                          <i className={`fas fa-clock ${styles.estadoIcono}`} /> Pendiente
                        </span>
                      )}
                    </td>
                    <td>
                      <div className={`actions ${styles.accionesCentro}`}>
                        <button
                          className={`btn btn-sm ${g.checked_in ? 'btn-secondary' : 'btn-success'} btn-icon ${g.checked_in ? styles.checkRevertir : styles.checkRegistrar}`}
                          onClick={() => handleCheckin(g)}
                          disabled={checkingIn === g.id}
                          title={g.checked_in ? 'Revertir check-in' : 'Registrar asistencia'}>
                          {checkingIn === g.id
                            ? <i className="fas fa-spinner fa-spin" />
                            : <i className={`fas ${g.checked_in ? 'fa-rotate-left' : 'fa-circle-check'} ${g.checked_in ? styles.glifoRevertir : styles.glifoRegistrar}`} />}
                        </button>
                        <button className="btn btn-sm btn-secondary btn-icon"
                          onClick={() => setBadgeGuest(g)} title="Ver escarapela">
                          <i className="fas fa-id-badge" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {/* Paginación — oculta mientras se está buscando */}
      {filterEvt && searchResults === null && !loading && <Paginacion pagina={page} total={total} porPagina={PAGE_SIZE} onCambiar={irAPagina} />}

      {importModal && (
        <ImportModal
          companies={companies}
          onClose={() => setImportModal(false)}
          onImported={() => load()}
        />
      )}
      {badgeGuest && (
        <Badge
          guest={badgeGuest}
          event={selectedEvent}
          company={selectedCompany}
          onClose={() => setBadgeGuest(null)}
        />
      )}
      {scannerOpen && (
        <QrScanner
          eventId={filterEvt}
          onCheckedIn={() => load(filterEvt, page)}
          onClose={() => setScannerOpen(false)}
        />
      )}
      {inviteModal && (
        <InviteModal
          eventId={filterEvt}
          selectedIds={[...selectedIds]}
          onClose={() => setInviteModal(false)}
          onSent={(message) => {
            setInviteModal(false);
            setAlert({ type: 'success', message });
            setSelectedIds(new Set());
          }}
        />
      )}
    </div>
  );
}